import React, { useState } from 'react'
import { useDispatch } from 'react-redux'
import { Grid } from '@material-ui/core'
import List from '@material-ui/core/List'
import ListItem from '@material-ui/core/ListItem'
import ListItemText from '@material-ui/core/ListItemText'
import Box from '@material-ui/core/Box'
import Typography from '@material-ui/core/Typography'
import LinearProgress from '@material-ui/core/LinearProgress'
import { makeStyles } from '@material-ui/core/styles'
import ToggleButton from '@material-ui/lab/ToggleButton'
import ToggleButtonGroup from '@material-ui/lab/ToggleButtonGroup'
import Checkbox from '@material-ui/core/Checkbox'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import DayPicker from 'material-ui/DatePicker'
import moment from 'moment'

import { hideReduxModal } from '../../../../Reducers/UI/actions'
import { number as numberValidation } from '../../../../Utils/Validators/formValidation'
import Form, { DateTimePicker } from '../../../Form'
import { Input } from '../../../Form/Input'

import SubmitButton from '../../../Form/SubmitButton'
import FormButton from '../../../Form/Button'
import { SectionLabel } from '../../../../Components/Section'
import { updateMissionSimplified } from '../../../../Module/graphQL'
import { BULK_BATCH_SIZE } from '../../../MissionsControls/utils'
import { getEditableFields } from '../../../../Containers/Form/Mission/EditMissionDetails/util'
import {
  TIME_OF_DAY,
  timeOfDayToTime,
} from '../../../../Utils/CityPlan/timeOfDay'
import { describeBulkResult } from '../../../MissionsControls/utils'
import WeekSelect from '../../../Form/SelectDashboard/types/WeekSelect/WeekSelect'
import {
  formatToLocalDate,
  getEndOfWeek,
} from '../../../../shared/libraries/utils/date'

// Stable, or the field re-registers with the form on every render.
const PAY_ADJUSTMENT_VALIDATORS = [numberValidation]

const useStyles = makeStyles(theme => ({
  list: {
    maxHeight: '40vh',
    overflow: 'scroll',
  },
}))

/**
 * Puts a time of day onto a picked date time: the date part stays, the time
 * becomes the city's check-in time for that part of the day.
 * @param {string} localDateTime "YYYY-MM-DDTHH:mm:ss.sss"
 * @param {?string} timeOfDay TIME_OF_DAY value
 * @param {object} city
 * @returns {string}
 */
export const applyTimeOfDay = (localDateTime, timeOfDay, city) => {
  if (!localDateTime || !timeOfDay) return localDateTime
  const day = String(localDateTime).slice(0, 10)
  return `${day} ${timeOfDayToTime(timeOfDay, city)}`
}

function BulkEditMissionModalBody({
  missionsToEdit,
  onClose = () => {},
  currencySign = '$',
  // Server-side mode (letterbox city plans): one call for the whole selection.
  onSubmitBulk = null,
  selectedCount = 0,
  city = {},
}) {
  const [submitButtonDisabled, setSubmitButtonDisabled] = useState(false)
  const [log, setLog] = useState([])
  const [loadingProgress, setLoadingProgress] = React.useState(0)
  const [activeDateInput, setActiveDateInput] = useState(false)
  const [timeOfDay, setTimeOfDay] = useState(null)
  // With a time of day chosen only the day is asked for (see the Date field).
  const [pickedDay, setPickedDay] = useState(null)
  // Several missions: number the typed label #1, #2 … or give them all
  // the same label (a van group shares one).
  const [numberLabels, setNumberLabels] = useState(true)

  const dispatch = useDispatch()
  const classes = useStyles()

  // Renaming one mission starts from its current label.
  const singleMissionLabel =
    selectedCount === 1 && missionsToEdit && missionsToEdit[0]
      ? missionsToEdit[0].label || ''
      : ''

  /**
   * One mutation for the whole selection; the API numbers labels and
   * reports what it refused.
   */
  const bulkEditServerSide = async convertedFormData => {
    setLoadingProgress(1)
    const patch = {}
    if (timeOfDay && pickedDay)
      patch.dateTime = `${moment(pickedDay).format(
        'YYYY-MM-DD',
      )} ${timeOfDayToTime(timeOfDay, city)}`
    else if (convertedFormData.dateTime)
      patch.dateTime = applyTimeOfDay(
        convertedFormData.dateTime,
        timeOfDay,
        city,
      )
    else if (convertedFormData.week)
      patch.dateTime = formatToLocalDate(
        getEndOfWeek(new Date(convertedFormData.week)),
      )
    if (
      convertedFormData.draftPayAdjustment &&
      String(convertedFormData.draftPayAdjustment).length
    )
      patch.draftPayAdjustment = Number(convertedFormData.draftPayAdjustment)
    // One mission is a rename: the label is kept exactly as typed. Several
    // get the typed label numbered #1, #2 … in table order.
    if (convertedFormData.label && convertedFormData.label.length) {
      if (selectedCount === 1 || !numberLabels)
        patch.label = convertedFormData.label
      else patch.labelWithIndex = convertedFormData.label
    }

    saveLogs(`💾 Updating ${selectedCount.toLocaleString()} missions…`)
    try {
      const result = await onSubmitBulk(patch)
      saveLogs(`✅ ${describeBulkResult(result, 'Updated')}`)
    } catch (error) {
      saveLogs(`❌ Missions were not updated. Reason: ${error.message}`)
    }
    setLoadingProgress(100)
  }

  const bulkEditHandler = async form => {
    const convertedFormData = Object.entries(form).reduce(
      (acc, [fieldName, { value }]) => {
        acc[fieldName] = value
        return acc
      },
      {},
    )
    if (onSubmitBulk) return bulkEditServerSide(convertedFormData)

    const progressSection = 100 / missionsToEdit.length
    setLoadingProgress(1)
    // Edits used to run strictly one mission at a time, which meant 3,000
    // sequential round trips for a full city plan. Run them in batches
    // instead; the label suffix still comes from the mission's original
    // position, so numbering does not depend on which request finishes first.
    const entries = [...missionsToEdit.entries()]
    let completed = 0

    for (let start = 0; start < entries.length; start += BULK_BATCH_SIZE) {
      const batch = entries.slice(start, start + BULK_BATCH_SIZE)

      await Promise.all(
        batch.map(async ([index, mission]) => {
          const humanReadableIndex = index + 1

          const editableFields = getEditableFields(
            mission.status,
            mission.type,
          ).filter(field =>
            ['dateTime', 'week', 'draftPayAdjustment', 'label'].includes(field),
          )
          editableFields.push('week')
          saveLogs(
            `#️⃣ ${humanReadableIndex}`,
            `💾 Saving mission with id: "${mission.id}"`,
            `📢 Editable fields for mission with status "${
              mission.status
            }" are: ${editableFields.join(', ')}`,
          )

          const missionDataInput = {
            missionInput: {
              id: mission.id,
              dateTime:
                editableFields.includes('dateTime') &&
                convertedFormData.dateTime
                  ? convertedFormData.dateTime
                  : editableFields.includes('week') && convertedFormData.week
                  ? formatToLocalDate(
                      getEndOfWeek(new Date(convertedFormData.week)),
                    )
                  : undefined,
              draftPayAdjustment:
                editableFields.includes('draftPayAdjustment') &&
                convertedFormData.draftPayAdjustment &&
                convertedFormData.draftPayAdjustment.length
                  ? Number(convertedFormData.draftPayAdjustment)
                  : undefined,
              label:
                convertedFormData.label && convertedFormData.label.length
                  ? `${convertedFormData.label}#${humanReadableIndex}`
                  : undefined,
            },
          }

          const options = { ignoreErrors: true }

          const response = await updateMissionSimplified(
            missionDataInput,
            options,
          )

          const result = await response.json()

          if (result.errors && result.errors.length) {
            saveLogs(
              `❌ Mission was not updated. Reason: ${result.errors[0].message}`,
            )
          } else {
            saveLogs(`✅ Mission updated succesfully`)
          }
        }),
      )

      completed += batch.length
      setLoadingProgress(Math.max(1, progressSection * completed))
    }
    setLoadingProgress(100)
  }

  const saveLogs = (...logs) => setLog(currentLog => [...currentLog, ...logs])

  const onCloseHandler = () => {
    dispatch(hideReduxModal())
    onClose()
  }

  return !loadingProgress ? (
    <Form
      onUpdate={(form, state) => {
        if (form.name == 'week' && form.value) setActiveDateInput(form.name)
        if (form.name == 'dateTime' && form.value) setActiveDateInput(form.name)
        // Save is allowed while no field is in error. (Judging by the last
        // field to register left it stuck: an untouched field re-registers
        // as invalid on every render.)
        setSubmitButtonDisabled(
          Object.values(state || {}).every(field => !field.error),
        )
      }}
      onSubmit={bulkEditHandler}
    >
      <Grid container spacing={5}>
        <Grid item xs={12}>
          <SectionLabel>Date</SectionLabel>
          {timeOfDay ? (
            // The time is settled, so only a day is asked for: no time
            // step after the calendar.
            <DayPicker
              name="pickedDay"
              hintText="Pick the day"
              autoOk
              value={pickedDay}
              disabled={activeDateInput == 'week'}
              formatDate={day => moment(day).format('DD/MM/YYYY')}
              onChange={(event, day) => {
                setPickedDay(day)
                setActiveDateInput('dateTime')
              }}
              textFieldStyle={{
                position: 'relative',
                width: '100%',
                borderRadius: '4px',
                height: '52px',
                background: '#F8F8F8',
                border: 'none',
                padding: '0 20px',
                outline: 'none',
              }}
            />
          ) : (
            <DateTimePicker
              name="dateTime"
              initValue={new Date()}
              disabled={activeDateInput == 'week'}
            />
          )}
          {onSubmitBulk && (
            <Box pt={1}>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={timeOfDay}
                onChange={(event, value) => {
                  setTimeOfDay(value)
                  if (!value) setPickedDay(null)
                }}
                aria-label="Time of day"
              >
                <ToggleButton
                  value={TIME_OF_DAY.MORNING}
                  disabled={activeDateInput == 'week'}
                >
                  Morning ({timeOfDayToTime(TIME_OF_DAY.MORNING, city)})
                </ToggleButton>
                <ToggleButton
                  value={TIME_OF_DAY.LUNCH}
                  disabled={activeDateInput == 'week'}
                >
                  Lunch ({timeOfDayToTime(TIME_OF_DAY.LUNCH, city)})
                </ToggleButton>
                <ToggleButton
                  value={TIME_OF_DAY.AFTERNOON}
                  disabled={activeDateInput == 'week'}
                >
                  Afternoon ({timeOfDayToTime(TIME_OF_DAY.AFTERNOON, city)})
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
          )}
        </Grid>
        <Grid item xs={12}>
          <SectionLabel>Week</SectionLabel>
          <WeekSelect
            name="week"
            disabled={activeDateInput == 'dateTime'}
            label={
              'BA will be able to select a date for his mission inside this week'
            }
          />
        </Grid>

        <Grid item xs={12}>
          <SectionLabel>Pay аdjustment ({currencySign})</SectionLabel>
          <Input
            style={{ width: '100%' }}
            name="draftPayAdjustment"
            validator={PAY_ADJUSTMENT_VALIDATORS}
          />
        </Grid>
        <Grid item xs={12}>
          <SectionLabel>Label</SectionLabel>
          <Input
            style={{ width: '100%' }}
            name="label"
            initValue={singleMissionLabel}
            placeholder={
              selectedCount > 1 && numberLabels
                ? 'Numbered #1, #2 … in table order'
                : ''
            }
          />
          {onSubmitBulk && selectedCount > 1 && (
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={!numberLabels}
                  onChange={event => setNumberLabels(!event.target.checked)}
                />
              }
              label={`Same label for all ${selectedCount.toLocaleString()} missions (no #1, #2 … numbering)`}
            />
          )}
        </Grid>
        <Grid item xs={12}>
          <Box pb={5} display="flex" justifyContent="space-between">
            <FormButton
              value="Cancel"
              onClick={() => dispatch(hideReduxModal())}
            />
            <SubmitButton disabled={!submitButtonDisabled} value="Save" />
          </Box>
        </Grid>
      </Grid>
    </Form>
  ) : (
    <div style={{ padding: 12 }}>
      <Grid container>
        <Grid item xs={12}>
          <LinearProgress variant="determinate" value={loadingProgress} />
          <Typography variant="h6">Log:</Typography>
          <List component="ul" className={classes.list}>
            {log.map(row => (
              <ListItem>
                <ListItemText primary={row} />
              </ListItem>
            ))}
          </List>
        </Grid>
        {loadingProgress === 100 && (
          <Grid item xs={12}>
            <Box display="flex" justifyContent="space-between">
              <FormButton value="Close" onClick={onCloseHandler} />
            </Box>
          </Grid>
        )}
      </Grid>
    </div>
  )
}

export default BulkEditMissionModalBody
